import React, { useState } from 'react';
import {
  Box,
  Chip,
  Paper,
  Typography,
  Collapse,
  IconButton,
  Divider,
  Checkbox,
  Tooltip,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditQuestionModal from './EditQuestionModal.tsx';
import MathRenderer from '../MathRenderer.tsx';

export interface QuestionOption {
  text: string;
  is_correct?: boolean;
  correct_reason?: string;
  incorrect_reason?: string;
}

export interface Question {
  id?: string | number;
  question_id?: string;
  question_bank_id?: string;
  company_id?: string;
  question_text?: string;
  question?: string;
  topic?: string;
  subtopic?: string;
  difficulty?: string;
  question_type?: string;
  options?: QuestionOption[] | string[];
  correct_answer?: string | string[];
  reasoning?: string;
  incorrect_reasoning?: string;
  verified?: boolean;
  accepted_by?: string | null;
  is_duplicate?: boolean;
  prerequisites?: string[];
  knowledgeMapData?: any[];
}

interface Props {
  question: Question;
  index: number;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  onQuestionUpdate?: (updated: Question) => void;
  knowledgeMapData?: any[];
}

const parseDifficulty = (raw?: string): string => {
  if (!raw) return '';
  return raw.replace(/[\[\]'"]/g, '').trim().toLowerCase();
};

const difficultyColor: Record<string, { bg: string; color: string; label: string }> = {
  easy: { bg: '#E8F5E9', color: '#2E7D32', label: 'Easy' },
  medium: { bg: '#FFF3E0', color: '#E65100', label: 'Medium' },
  hard: { bg: '#FFEBEE', color: '#C62828', label: 'Hard' },
};

const QuestionCard: React.FC<Props> = ({
  question,
  index,
  selectable = false,
  selected = false,
  onSelect,
  onQuestionUpdate,
  knowledgeMapData = [],
}) => {
  const [open, setOpen] = useState(false);
  const [prereqOpen, setPrereqOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [localQuestion, setLocalQuestion] = useState<Question>(question);

  const difficulty = parseDifficulty(question.difficulty);
  const dc = difficultyColor[difficulty] || { bg: '#F5F5F5', color: '#555', label: difficulty || 'Unknown' };
  const questionText = question.question_text || question.question || '';

  const handleQuestionSave = (updated: Question) => {
    setLocalQuestion(updated);
    onQuestionUpdate?.(updated);
  };

  // Get all options with their explanations
  const getOptionsWithExplanations = () => {
    if (!question.options || !Array.isArray(question.options)) return [];
    
    return question.options.map((option: any, idx: number) => {
      const optionText: string = typeof option === 'string' ? option : (option?.text ?? JSON.stringify(option));
      const isCorrect: boolean = typeof option === 'object'
        ? !!option?.is_correct
        : Array.isArray(question.correct_answer)
          ? question.correct_answer.includes(optionText)
          : optionText === question.correct_answer;
      
      // Get explanation based on option type
      let explanation = '';
      if (typeof option === 'object') {
        explanation = isCorrect ? option.correct_reason : option.incorrect_reason;
      }
      
      return {
        letter: String.fromCharCode(65 + idx),
        text: optionText,
        isCorrect,
        explanation,
        raw: option
      };
    });
  };

  const optionsWithExplanations = getOptionsWithExplanations();
  const hasAnyExplanation = optionsWithExplanations.some(opt => opt.explanation);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: question.verified ? '#C8E6C9' : '#E0E0E0',
          borderRadius: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          '&:hover': { 
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            borderColor: '#BDBDBD'
          },
          mb: 1.5,
        }}
      >
        {/* Ultra Compact Header */}
        <Box sx={{ 
          px: 1.5, 
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: '#FAFAFA',
          borderBottom: '1px solid #EEEEEE'
        }}>
          {/* Question Number - Small Circle */}
          <Box sx={{ 
            minWidth: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: '#1565C0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem'
          }}>
            {index + 1}
          </Box>

          {/* Question Text - Single Line with Ellipsis */}
          <Typography 
            sx={{ 
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: '#1E293B',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={questionText}
          >
            <MathRenderer content={questionText} />
          </Typography>

          {/* Action Icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* <Tooltip title="Details" arrow>
              <IconButton size="small" sx={{ p: 0.5 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: '#64748B' }} />
              </IconButton>
            </Tooltip> */}
            
            <Tooltip title="Edit" arrow>
              <IconButton size="small" onClick={() => setEditModalOpen(true)} sx={{ p: 0.5 }}>
                <EditOutlinedIcon sx={{ fontSize: 16, color: '#64748B' }} />
              </IconButton>
            </Tooltip>

            {selectable && (
              <Checkbox 
                size="small" 
                checked={selected} 
                onChange={onSelect}
                sx={{ p: 0.5 }}
              />
            )}
          </Box>
        </Box>

        {/* Options Grid - With Metadata on Right */}
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {/* Options Column - Takes 70% space */}
            <Box sx={{ flex: '0 0 70%' }}>
              <Stack spacing={0.5}>
                {optionsWithExplanations.map((option, idx) => (
                  <Box key={idx}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: option.isCorrect ? '#F0FDF4' : '#FFFFFF',
                        border: '1px solid',
                        borderColor: option.isCorrect ? '#BBF7D0' : '#E2E8F0',
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, minWidth: 18, color: option.isCorrect ? '#166534' : '#64748B' }}>
                        {option.letter}.
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', flex: 1, color: option.isCorrect ? '#14532D' : '#334155' }}>
                         <MathRenderer content={option.text} />
                      </Typography>
                      {option.isCorrect ? (
                        <CheckCircleIcon sx={{ fontSize: 14, color: '#22C55E' }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: 14, color: '#EF4444' }} />
                      )}
                    </Box>
                    
                    {/* Show explanation for this option if it exists */}
                    {open && option.explanation && (
                      <Box sx={{ 
                        mt: 0.5, 
                        ml: 3, 
                        mr: 1, 
                        p: 0.75, 
                        bgcolor: option.isCorrect ? '#F0FDF4' : '#FEF2F2',
                        borderRadius: 1,
                        borderLeft: '2px solid',
                        borderLeftColor: option.isCorrect ? '#4CAF50' : '#EF4444',
                        fontSize: '0.6875rem',
                        color: option.isCorrect ? '#166534' : '#991B1B',
                      }}>
                        <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.625rem' }}>
                          {option.isCorrect ? '✓ WHY CORRECT: ' : '✗ WHY INCORRECT: '}
                        </Typography>
                       <MathRenderer content={option.explanation} />
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Metadata Column - Takes 30% space, right-aligned */}
            <Box sx={{ 
              flex: '0 0 30%', 
              display: 'flex', 
              flexDirection: 'column',
              gap: 0.5,
              alignItems: 'flex-end'
            }}>
              {/* Topic Chip */}
              {question.topic && (
                <Chip
                  label={question.topic}
                  size="small"
                  sx={{ 
                    height: 20,
                    fontSize: '0.625rem',
                    bgcolor: '#E3F2FD',
                    color: '#1565C0',
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
              )}
              
              {/* Subtopic Chip */}
              {question.subtopic && (
                <Chip
                  label={question.subtopic}
                  size="small"
                  sx={{ 
                    height: 20,
                    fontSize: '0.625rem',
                    bgcolor: '#F3E5F5',
                    color: '#7B1FA2',
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
              )}
              
              {/* Difficulty Chip */}
              <Chip
                label={dc.label}
                size="small"
                sx={{ 
                  height: 20,
                  fontSize: '0.625rem',
                  bgcolor: dc.bg,
                  color: dc.color,
                  fontWeight: 500,
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />

              {/* Verified Badge with Username - Now showing full text */}
              {question.verified && (
                <Tooltip 
                  title={question.accepted_by ? `Verified by ${question.accepted_by}` : 'Verified'}
                  arrow
                >
                  <Chip
                    icon={<VerifiedIcon sx={{ fontSize: 12 }} />}
                    label={question.accepted_by || 'Verified'}
                    size="small"
                    sx={{ 
                      height: 20,
                      fontSize: '0.625rem',
                      bgcolor: '#E8F5E9',
                      color: '#2E7D32',
                      '& .MuiChip-label': { px: 0.75 },
                      '& .MuiChip-icon': { ml: 0.5, fontSize: 12 }
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        {/* Explanation Toggle - Only show if any explanations exist */}
        {hasAnyExplanation && (
          <>
            <Divider sx={{ my: 0 }} />
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                bgcolor: '#F8FAFC',
                '&:hover': { bgcolor: '#F1F5F9' },
              }}
              onClick={() => setOpen(!open)}
            >
              <LightbulbOutlinedIcon sx={{ fontSize: 14, color: '#F59E0B', mr: 0.5 }} />
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, color: '#475569' }}>
                {open ? 'Hide' : 'Show'} Option Explanations
              </Typography>
              <IconButton size="small" sx={{ ml: 'auto', p: 0.25 }}>
                {open ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </Box>
          </>
        )}

        {/* Prerequisites - Ultra Compact */}
        {question.prerequisites && question.prerequisites.length > 0 && (
          <>
            <Divider sx={{ my: 0 }} />
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                bgcolor: '#F8FAFC',
                '&:hover': { bgcolor: '#F1F5F9' },
              }}
              onClick={() => setPrereqOpen(!prereqOpen)}
            >
              <LightbulbOutlinedIcon sx={{ fontSize: 14, color: '#3B82F6', mr: 0.5 }} />
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, color: '#475569' }}>
                {prereqOpen ? 'Hide' : 'View'} Prerequisites
              </Typography>
              <IconButton size="small" sx={{ ml: 'auto', p: 0.25 }}>
                {prereqOpen ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </Box>

            <Collapse in={prereqOpen}>
              <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {question.prerequisites.map((item, idx) => (
                    <Chip
                      key={idx}
                      label={item}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.625rem',
                        bgcolor: '#FFFFFF',
                        color: '#2563EB',
                        border: '1px solid #BFDBFE',
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Collapse>
          </>
        )}
      </Paper>

      <EditQuestionModal
        open={editModalOpen}
        question={localQuestion}
        onClose={() => setEditModalOpen(false)}
        onSave={handleQuestionSave}
        knowledgeMapData={knowledgeMapData}
      />
    </>
  );
};

export default QuestionCard;
